import React from "react"
import { Box, Paper, Grid, IconButton } from "@mui/material"

import { makeStyles } from "makeStyles"

import Link from "components/i18n/Link"
import Price from "components/Price"
import LazyLoadedImage from "components/LazyLoadedImage"
import ProductHitStructuredData from "@secureo/common/components/StructuredData/ProductHitStructuredData"
import ListViewItemAttributes from "./Product/ListViewItemAttributes"

import useProductHitImpressionTracking from "@secureo/common/hooks/tracking/useProductHitImpressionTracking"

import getDeliveryTimeWorkDaysString from "@secureo/common/utils/commerceTools/formatters/getDeliveryTimeWorkDaysString"
import { trackProductHitClick } from "@secureo/common/utils/tracking/productHit"

import { AlgoliaProductHit } from "@secureo/common/typings/Algolia"

const useStyles = makeStyles()({
	attributes: {
		visibility: "hidden",
		marginTop: "-120px",
		opacity: 0.4,
	},
	button: {
		background: "#E3E5E6",
		width: 46,
		height: 46,
	},
	title: {
		height: 80,
		marginBottom: -20,
		color: "black",
		cursor: "pointer",
	},
	product: {
		boxShadow: "none",
		border: "1px solid whitesmoke",
		borderRadius: 10,
		// transition: "0.3s",
		position: "relative",
		height: 330,
		"&:hover": {
			position: "absolute",
			boxShadow: "0px 0px 5px 0px rgba(0,0,0,0.35)",
			height: 410,
			border: "1px solid #4691CE",
			transition: "0s",
			zIndex: 1,
			"& $attributes": {
				visibility: "visible",
				marginTop: 10,
				marginBottom: -20,
				opacity: 1,
				"& ul": {
					listStyleType: "square",
					marginLeft: 15,
				},
			},
			"& $button": {
				background: "#4691CE",
				color: "white",
				boxShadow: "0px 0px 5px 0px rgba(0,0,0,0.35)",
			},
			"& $title": {
				color: "#4691CE",
			},
		},
	},
	badge: {
		position: "absolute",
		top: 15,
		left: 15,
		fontSize: 10,
		textTransform: "uppercase",
		border: "1px solid #4691CE",
		color: "#4691CE",
		borderRadius: 5,
		paddingLeft: 5,
		paddingRight: 5,
		"&:hover": {
			color: "white",
			background: "#4691CE",
			cursor: "pointer",
		},
	},
})

const GridViewItem = ({ hit, locale, categoryName, listName = "", position }: Props) => {
	const { classes } = useStyles()
	const trackProductHitImpression = useProductHitImpressionTracking(
		hit,
		categoryName,
		listName || categoryName,
		position,
	)

	const { name, grossPriceCents, image, deliveryTimeWorkdays, slug, manufacturer, productBadge } =
		hit

	const cutProductName = (name = "") => (name.length > 53 ? `${name.substring(0, 53)} ...` : name)

	const onClick = () => {
		trackProductHitClick(
			name,
			grossPriceCents,
			manufacturer,
			categoryName,
			listName || categoryName,
			position,
		)
	}

	return (
		<Link href={`/p/${slug}`} onClick={onClick}>
			<div style={{ position: "relative" }}>
				<Paper className={classes.product}>
					<Box p={2}>
						<Grid container spacing={3}>
							{/* Product Image */}
							<Grid item xs={12} md={12} style={{ textAlign: "center" }}>
								<LazyLoadedImage
									alt={name}
									title={name}
									src={image}
									style={{
										height: 130,
										width: 150,
										objectFit: "contain",
										margin: "auto",
										marginBottom: -20,
									}}
									onIntersection={trackProductHitImpression}
								/>
							</Grid>
							<Grid item xs={12} md={12} style={{ margin: 10 }}>
								{/* title */}
								<div className={classes.title}>
									<strong>{cutProductName(name)}</strong>
								</div>
								{/* attributes */}
								<div className={classes.attributes}>
									<ListViewItemAttributes hit={hit} />
									<div style={{ borderBottom: "2px dottet grey" }} />
								</div>
							</Grid>
							{/* Price & CTA Button */}
							<Grid item xs={12}>
								<div style={{ display: "flex", justifyContent: "space-between" }}>
									<Box ml={1}>
										<strong style={{ fontSize: 22, marginRight: 5 }}>
											<Price priceInEuroCents={grossPriceCents} />
										</strong>
										<span style={{ color: "grey", fontSize: 12 }}>
											inkl. MwSt.
										</span>
										<br />
										<Box style={{ fontSize: 12 }}>
											Lieferzeit:{" "}
											{getDeliveryTimeWorkDaysString(deliveryTimeWorkdays)}
										</Box>
									</Box>
									<IconButton
										aria-label="delete"
										className={classes.button}
										size="large"
									>
										&rarr;
									</IconButton>
								</div>
							</Grid>
						</Grid>
					</Box>

					{productBadge && (
						<div className={classes.badge}>
							<strong>{productBadge}</strong>
						</div>
					)}
				</Paper>
				<ProductHitStructuredData product={hit} locale={locale} />
			</div>
		</Link>
	)
}

interface Props {
	hit: AlgoliaProductHit
	locale: string
	position: number
	categoryName: string
	listName?: string
}

export default GridViewItem
